// ─── Écran COURSES : liste de courses de l'espace ──────────────────
// Alimentée manuellement ou depuis une recette/un repas (bouton 🛒 sur leur
// fiche, cf. FicheRecette/FicheRepas). Coche pour rayer au fur et à mesure ;
// pas de tri par rayon pour l'instant (référentiel séparé, plus tard).
//
// `onFermer` optionnel (17/08) : dans My Meals (onglet dédié), l'écran vit au
// niveau racine et n'a pas besoin de retour — dans My Calendar (hub Famille),
// il est posé en calque par-dessus le hub, comme Journal/Contacts/Lieux, et
// a besoin du geste + de la flèche de retour. `GesteRetourEcran`/`PageHeader`
// désactivent proprement le geste/la flèche quand `onFermer` est absent (cf.
// leur code : `actif={!!onFermer}`, `{onRetour ? … : …}`) — comportement de
// My Meals inchangé.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet, Alert } from 'react-native';
import { PageHeader, EmptyState, LigneSwipe, GesteRetourEcran, HIT, useUI, useT, useEspace, confirmerSuppression } from '@mop/core';
import {
  ecouterListeCourses, enregistrerListeCourses, fusionnerDansListe, basculerCoche, retirerItem, viderCoches,
} from './services/listeCourses';
import FeuilleItemCourse from './FeuilleItemCourse';

export default function EcranCourses({ onFermer } = {}) {
  const UI = useUI();
  const t = useT();
  const { tenantId, assurerEspace } = useEspace();
  const s = useMemo(() => makeStyles(UI), [UI]);

  const [items, setItems] = useState([]);
  const [feuille, setFeuille] = useState(false);
  const [erreur, setErreur] = useState('');

  useEffect(() => ecouterListeCourses(tenantId, (liste) => { setErreur(''); setItems(liste); },
    (e) => setErreur(`courses — ${e?.message || e?.code || 'erreur'}`)), [tenantId]);

  const echec = (e) => Alert.alert(t('repas.erreur'), e?.message || '');
  const majListe = async (suivant) => {
    try {
      const espaceId = tenantId || await assurerEspace();
      await enregistrerListeCourses(espaceId, suivant);
    } catch (e) { echec(e); }
  };

  const ajouterManuel = (f) => {
    majListe(fusionnerDansListe(items, [{ nom: f.nom, quantite: f.quantite, unite: f.unite }]));
    setFeuille(false);
  };

  const restants = items.filter(i => !i.coche);
  const coches = items.filter(i => i.coche);

  const ligne = (i) => (
    <LigneSwipe key={i.id} espacement={8} enabled onSupprimer={() => majListe(retirerItem(items, i.id))}>
      <TouchableOpacity style={s.carte} activeOpacity={0.7} onPress={() => majListe(basculerCoche(items, i.id))}>
        <Text style={{ fontSize: 18 }}>{i.coche ? '✅' : '⬜'}</Text>
        <Text style={[s.nom, i.coche && s.nomCoche]} numberOfLines={1}>
          {[i.quantite, i.unite, i.nom].filter(Boolean).join(' ')}
        </Text>
      </TouchableOpacity>
    </LigneSwipe>
  );

  return (
    <GesteRetourEcran actif={!!onFermer} onRetour={onFermer}>
      <View style={{ flex: 1, backgroundColor: UI.bg }}>
        <PageHeader titre={t('courses.titre')} iconeTitre="🛒" onRetour={onFermer}
          action={{ icone: '＋', label: t('courses.ajouterArticle'), onPress: () => setFeuille(true) }}>
          {coches.length > 0 && (
            <TouchableOpacity onPress={() => majListe(viderCoches(items))} hitSlop={HIT} accessibilityLabel={t('courses.viderCoches')}>
              <Text style={s.headerIcone}>🗑</Text>
            </TouchableOpacity>
          )}
        </PageHeader>

        <ScrollView contentContainerStyle={s.corps}>
          {!!erreur && (
            <TouchableOpacity style={s.erreur} activeOpacity={0.8} onPress={() => setErreur('')}>
              <Text style={s.erreurTxt}>⚠️ {erreur}</Text>
            </TouchableOpacity>
          )}

          {items.length === 0 ? (
            <EmptyState icon="🛒" texte={t('courses.aucune')} aide={t('courses.aucuneAide')} />
          ) : (
            <>
              {restants.map(ligne)}
              {coches.length > 0 && (
                <>
                  <Text style={s.sect}>{t('courses.coches', { n: coches.length })}</Text>
                  {coches.map(ligne)}
                </>
              )}
            </>
          )}
        </ScrollView>

        <FeuilleItemCourse visible={feuille} onEnregistrer={ajouterManuel} onFermer={() => setFeuille(false)} />
      </View>
    </GesteRetourEcran>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  corps: { padding: 12, paddingBottom: 30 },
  headerIcone: { fontSize: 19, color: UI.accent },
  carte: { flexDirection: 'row', alignItems: 'center', gap: 12, backgroundColor: UI.card, borderRadius: 14, borderWidth: 0.5, borderColor: UI.border, padding: 13 },
  nom: { flex: 1, fontSize: 14.5, color: UI.ink, fontWeight: '600' },
  nomCoche: { color: UI.faint, textDecorationLine: 'line-through' },
  sect: { fontSize: 12, fontWeight: '800', color: UI.faint, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 16, marginBottom: 8 },
  erreur: { backgroundColor: UI.card, borderRadius: 12, borderWidth: 1, borderColor: UI.danger || '#c0392b', padding: 11, marginBottom: 10 },
  erreurTxt: { fontSize: 13, color: UI.danger || '#c0392b', fontWeight: '700' },
});
